import { Circle, Group, Line, Rect } from "react-konva";
import type { Dims } from "@/lib/canvas/coords";

/**
 * Soccer-pitch background, rendered directly via react-konva
 * primitives so the canvas stays a single layer and we don't have to
 * load an external SVG / image asset on boot.
 *
 * The pitch is drawn in landscape attacking left-to-right (matching
 * the legacy convention used by `FORMATION_PRESETS`). All coordinates
 * here are in pixels, scaled to the `dims` argument. Proportions are
 * derived from a real soccer pitch:
 *
 *   - Outer touchline padding: 4px so the line strokes don't clip
 *   - Halfway line: centered vertically (x = 0.5 of dims.width)
 *   - Centre circle: r = 0.097 of the longer axis
 *   - Penalty boxes: roughly 0.165 wide, 0.394 tall (from each goal
 *     line, centered vertically)
 *   - 6-yard boxes: 0.055 wide, 0.198 tall
 *
 * Caller is responsible for the listening: true/false hint — this
 * component is a pure decorative layer.
 */

export type PitchBackgroundProps = {
  dims: Dims;
  /** Optional override for the field color. Default matches the
   * legacy pitch fill (a deep green that reads on both dark + light
   * theme). */
  color?: string;
  /** Optional override for line color. */
  lineColor?: string;
};

const DEFAULT_FIELD = "#1f6f4a";
const DEFAULT_LINES = "rgba(255, 255, 255, 0.78)";

export function PitchBackground(props: PitchBackgroundProps) {
  const { dims } = props;
  const fill = props.color ?? DEFAULT_FIELD;
  const stroke = props.lineColor ?? DEFAULT_LINES;
  const { width: W, height: H } = dims;

  const pad = 4;
  const innerLeft = pad;
  const innerTop = pad;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const cx = innerLeft + innerW / 2;
  const cy = innerTop + innerH / 2;
  const centreR = Math.min(innerW, innerH) * 0.097;

  // Penalty boxes
  const pBoxW = innerW * 0.165;
  const pBoxH = innerH * 0.394;
  const pBoxYOffset = (innerH - pBoxH) / 2;

  // 6-yard boxes
  const sBoxW = innerW * 0.055;
  const sBoxH = innerH * 0.198;
  const sBoxYOffset = (innerH - sBoxH) / 2;

  // Goal lines (thin notches outside the playing area, purely
  // decorative).
  const goalW = innerW * 0.014;
  const goalH = innerH * 0.105;
  const goalYOffset = (innerH - goalH) / 2;

  return (
    <Group listening={false}>
      <Rect
        x={0}
        y={0}
        width={W}
        height={H}
        fill={fill}
        cornerRadius={6}
      />
      {/* Touchlines */}
      <Rect
        x={innerLeft}
        y={innerTop}
        width={innerW}
        height={innerH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Halfway line */}
      <Line
        points={[cx, innerTop, cx, innerTop + innerH]}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Centre circle + spot */}
      <Circle x={cx} y={cy} radius={centreR} stroke={stroke} strokeWidth={2} />
      <Circle x={cx} y={cy} radius={3} fill={stroke} />
      {/* Left penalty box */}
      <Rect
        x={innerLeft}
        y={innerTop + pBoxYOffset}
        width={pBoxW}
        height={pBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Right penalty box */}
      <Rect
        x={innerLeft + innerW - pBoxW}
        y={innerTop + pBoxYOffset}
        width={pBoxW}
        height={pBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Left 6-yard box */}
      <Rect
        x={innerLeft}
        y={innerTop + sBoxYOffset}
        width={sBoxW}
        height={sBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Right 6-yard box */}
      <Rect
        x={innerLeft + innerW - sBoxW}
        y={innerTop + sBoxYOffset}
        width={sBoxW}
        height={sBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Penalty spots */}
      <Circle
        x={innerLeft + innerW * 0.11}
        y={cy}
        radius={3}
        fill={stroke}
      />
      <Circle
        x={innerLeft + innerW * 0.89}
        y={cy}
        radius={3}
        fill={stroke}
      />
      {/* Goals (decorative notches outside touchlines) */}
      <Rect
        x={innerLeft - goalW}
        y={innerTop + goalYOffset}
        width={goalW}
        height={goalH}
        stroke={stroke}
        strokeWidth={2}
      />
      <Rect
        x={innerLeft + innerW}
        y={innerTop + goalYOffset}
        width={goalW}
        height={goalH}
        stroke={stroke}
        strokeWidth={2}
      />
    </Group>
  );
}
